import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronRight, BookOpen, ArrowLeft, ExternalLink } from 'lucide-react';
import { T } from '../theme';
import { HELP_SECTIONS, HELP, getHelpBySection, searchHelp } from '../helpContent';
import { Card } from '../components/UI';

export default function HelpPage() {
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState({});

  const searchResults = useMemo(() => {
    if (!search || search.length < 2) return null;
    return searchHelp(search);
  }, [search]);

  const toggleEntry = (id) => {
    setExpandedEntries(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} style={{ height: 8 }} />;
      // Bold
      let processed = trimmed.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
      // Bullet
      const isBullet = processed.startsWith("•") || processed.startsWith("-");
      if (isBullet) {
        const content = processed.replace(/^[•\-]\s*/, "");
        return (
          <div key={i} style={{ display: "flex", gap: 8, paddingLeft: 8, marginBottom: 4 }}>
            <span style={{ color: T.cyan, flexShrink: 0 }}>•</span>
            <span style={{ color: T.textDim, fontSize: 12, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        );
      }
      // Numbered
      const numMatch = processed.match(/^(\d+)\.\s*(.*)/);
      if (numMatch) {
        return (
          <div key={i} style={{ display: "flex", gap: 8, paddingLeft: 8, marginBottom: 4 }}>
            <span style={{ color: T.amber, fontWeight: 700, flexShrink: 0, fontSize: 11 }}>{numMatch[1]}.</span>
            <span style={{ color: T.textDim, fontSize: 12, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: numMatch[2] }} />
          </div>
        );
      }
      return <div key={i} style={{ color: T.textDim, fontSize: 12, lineHeight: 1.6, marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: processed }} />;
    });
  };

  const renderEntry = (entry) => {
    const isOpen = expandedEntries[entry.id];
    return (
      <div key={entry.id} style={{ marginBottom: 6, border: `1px solid ${isOpen ? T.cyan + "30" : T.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s" }}>
        {/* Header */}
        <div onClick={() => toggleEntry(entry.id)}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", background: isOpen ? T.cyan + "08" : "transparent" }}
          onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = T.border + "30"; }}
          onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}>
          {isOpen ? <ChevronDown size={16} style={{ color: T.cyan, flexShrink: 0 }} /> : <ChevronRight size={16} style={{ color: T.textMuted, flexShrink: 0 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: isOpen ? T.cyan : T.text }}>{entry.title}</div>
            {!isOpen && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, lineHeight: 1.4 }}>{entry.short}</div>}
          </div>
          <span style={{ fontSize: 9, color: T.textMuted, background: T.bg, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
            {HELP_SECTIONS.find(s => s.id === entry.section)?.label || entry.section}
          </span>
        </div>
        {/* Expanded content */}
        {isOpen && (
          <div style={{ padding: "0 16px 16px 42px" }}>
            {/* Short summary highlighted */}
            <div style={{ background: T.cyan + "08", border: `1px solid ${T.cyan}15`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: T.cyan, lineHeight: 1.5, fontWeight: 500 }}>
              {entry.short}
            </div>
            {/* Full description */}
            {entry.full && (
              <div style={{ marginBottom: entry.formula || entry.example ? 12 : 0 }}>
                {renderMarkdown(entry.full)}
              </div>
            )}
            {/* Formula box */}
            {entry.formula && (
              <div style={{ marginBottom: entry.example ? 12 : 0 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.amber, letterSpacing: 1, marginBottom: 6 }}>Formula</div>
                <div style={{ background: T.bg, border: `1px solid ${T.amber}20`, borderRadius: 8, padding: "10px 14px", fontFamily: "monospace", fontSize: 11, color: T.amber, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {entry.formula}
                </div>
              </div>
            )}
            {/* Example box */}
            {entry.example && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.green, letterSpacing: 1, marginBottom: 6 }}>Example</div>
                <div style={{ background: T.green + "08", border: `1px solid ${T.green}20`, borderRadius: 8, padding: "10px 14px", fontSize: 11, color: T.green, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {entry.example}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Section detail view
  if (activeSection) {
    const section = HELP_SECTIONS.find(s => s.id === activeSection);
    const entries = getHelpBySection(activeSection);
    return (
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <button onClick={() => setActiveSection(null)}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: T.cyan, cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 16, padding: 0 }}>
          <ArrowLeft size={16} /> Back to Help Center
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 28 }}>{section?.icon}</span>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{section?.label}</div>
            <div style={{ fontSize: 12, color: T.textDim }}>{entries.length} topics</div>
          </div>
        </div>
        {entries.map(renderEntry)}
      </div>
    );
  }

  // Main help page
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
          <BookOpen size={28} style={{ color: T.cyan }} />
          <span style={{ fontSize: 24, fontWeight: 800, color: T.text }}>Help Center</span>
        </div>
        <div style={{ fontSize: 13, color: T.textDim }}>Everything you need to understand the Clients Profiling system</div>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 24 }}>
        <Search size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.textMuted }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search topics... (e.g., 'risk score', 'scalper', 'B-Book', 'arbitrage')"
          style={{ width: "100%", padding: "12px 16px 12px 42px", borderRadius: 10, border: `1px solid ${search ? T.cyan + "40" : T.border}`, background: T.bgInput, color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
        {search && (
          <button onClick={() => setSearch("")}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16 }}>×</button>
        )}
      </div>

      {/* Search Results */}
      {searchResults && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textDim, marginBottom: 10 }}>
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{search}"
          </div>
          {searchResults.length === 0 && (
            <Card>
              <div style={{ textAlign: "center", padding: 20, color: T.textMuted }}>
                No topics found. Try different keywords.
              </div>
            </Card>
          )}
          {searchResults.map(renderEntry)}
        </div>
      )}

      {/* Section Grid */}
      {!searchResults && (
        <>
          {/* Quick Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
            {[
              { label: "Sections", value: HELP_SECTIONS.length, color: T.cyan },
              { label: "Topics", value: Object.keys(HELP).length, color: T.green },
              { label: "Risk Factors", value: "113", color: T.amber },
              { label: "Client Groups", value: "7", color: T.purple },
            ].map(s => (
              <div key={s.label} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Section Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {HELP_SECTIONS.map(section => {
              const entries = getHelpBySection(section.id);
              return (
                <div key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 18px", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.cyan + "40"; e.currentTarget.style.background = T.cyan + "05"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bgCard; }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>{section.icon}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{section.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: T.textDim, marginBottom: 10, lineHeight: 1.4 }}>
                    {entries.slice(0, 3).map(e => e.title).join(" · ")}
                    {entries.length > 3 && ` · +${entries.length - 3} more`}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: T.cyan, fontWeight: 600 }}>{entries.length} topics</span>
                    <ExternalLink size={12} style={{ color: T.textMuted }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Access: Most Important Topics */}
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.8 }}>Essential Topics</div>
            {["riskScore", "groupSystem", "riskLevels", "behavioralRisk", "equityRisk", "arbOverview", "speedOverview", "glossABook", "glossBBook"].map(id => {
              const entry = HELP[id];
              if (!entry) return null;
              return renderEntry({ id, ...entry });
            })}
          </div>
        </>
      )}
    </div>
  );
}
